import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Dashboard.module.css';
import Card from '../components/Card';
import StatCard from '../components/StatCard';
import Badge from '../components/Badge';
import Button from '../components/Button';
import Table from '../components/Table';
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import UsageMeter from '../components/UsageMeter';
import { useSubscription, useTransactions, useTrialStatus, useLicenseTokens } from '../hooks';
import { useAuth } from '../store/AuthContext';
import { formatCurrency, formatDate, formatNumber, getDaysRemaining } from '../utils/helpers';
import {
  SubscriptionIcon,
  CalendarIcon,
  DongleIcon,
  TransactionIcon,
  CheckCircleIcon,
} from '../components/Icons';

const MAX_LICENCES_SHOWN = 5;

function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { subscription, loading: subLoading } = useSubscription();
  const { transactions, loading: txLoading } = useTransactions();
  const { trial, loading: trialLoading } = useTrialStatus();
  const { usage, documents, loading: docsLoading } = useLicenseTokens();

  const loading = subLoading || txLoading || trialLoading || docsLoading;
  const recentTransactions = transactions.slice(0, 5);

  const getStatusBadge = (status) => {
    const variants = {
      active: 'success',
      expired: 'error',
      pending: 'warning',
      completed: 'success',
      paid: 'success',
      failed: 'error',
    };
    return <Badge variant={variants[status?.toLowerCase()] || 'default'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Skeleton variant="rectangular" height={24} width={200} />
          <Skeleton variant="rectangular" height={14} width={300} />
        </div>
        <div className={styles.statsGrid}>
          {[1, 2, 3, 4].map(i => (
            <Skeleton.Card key={i} lines={2} />
          ))}
        </div>
      </div>
    );
  }

  // With no active subscription, fall back to the (expired) trial so its dates still show
  const showExpiredTrial = !subscription && trial.state === 'expired';
  const isTrial = subscription?.is_trial || showExpiredTrial;

  const daysRemaining = subscription?.end_date ? getDaysRemaining(subscription.end_date) : 0;
  const isExpiringSoon = !isTrial && daysRemaining > 0 && daysRemaining <= 7;

  // Map API response fields to display fields
  const planName = isTrial
    ? 'Free Trial'
    : subscription?.plan?.name || subscription?.plan_name || 'No Plan';
  const dongleCount = showExpiredTrial
    ? trial.licences
    : subscription?.number_of_dongles || subscription?.dongle_count || 0;
  const endDate = showExpiredTrial ? trial.endDate : subscription?.end_date;
  const subStatus = showExpiredTrial
    ? 'Expired'
    : subscription?.is_trial && daysRemaining === 0 ? 'Expired' : subscription?.status;
  const trialExpired = isTrial && (showExpiredTrial || daysRemaining === 0);
  const firstName = (user?.name || '').trim().split(' ')[0];

  const licenceList = documents?.licences || [];
  const activatedLicences = usage?.used_licences || 0;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>{firstName ? `Welcome back, ${firstName}` : 'Dashboard'}</h1>
        <p className={styles.subtitle}>Your plan, licences and document usage at a glance</p>
      </header>

      {/* Alert Banner */}
      {isExpiringSoon && (
        <div className={styles.alertBanner}>
          <span>Your subscription expires in {daysRemaining} days.</span>
          <Button size="sm" onClick={() => navigate('/plans')}>
            Renew Now
          </Button>
        </div>
      )}

      {/* Free trial banner */}
      {isTrial && (
        <div className={`${styles.trialBanner} ${trialExpired ? styles.trialBannerExpired : ''}`}>
          <div className={styles.trialBannerText}>
            <strong>
              {trialExpired ? 'Your free trial has expired' : `Free trial · ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left`}
            </strong>
            <span>
              {trialExpired
                ? `Expired on ${endDate ? formatDate(endDate) : '—'}. Choose a plan to keep signing documents.`
                : `Your trial ends on ${endDate ? formatDate(endDate) : '—'}. Upgrade anytime to keep access without interruption.`}
            </span>
            {!trialExpired && documents?.is_trial && (
              <span>
                You have signed <strong>{formatNumber(documents.documents_used)}</strong> of{' '}
                {formatNumber(documents.document_limit)} free documents.
              </span>
            )}
          </div>
          <Button size="sm" onClick={() => navigate('/plans')}>
            {trialExpired ? 'Choose a Plan' : 'Upgrade Now'}
          </Button>
        </div>
      )}

      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        <StatCard
          title="Current Plan"
          value={planName}
          subtitle={subStatus ? getStatusBadge(subStatus) : null}
          icon={<SubscriptionIcon />}
          variant="primary"
        />
        <StatCard
          title={trialExpired ? 'Expired On' : 'Valid Till'}
          value={endDate ? formatDate(endDate) : '—'}
          subtitle={
            endDate
              ? daysRemaining > 0 ? `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left` : 'Expired'
              : null
          }
          icon={<CalendarIcon />}
          variant={daysRemaining > 0 && daysRemaining <= 7 ? 'error' : 'success'}
        />
        <StatCard
          title="Licences"
          value={dongleCount}
          subtitle={dongleCount ? `${activatedLicences} activated on a computer` : 'No licences yet'}
          icon={<DongleIcon />}
          variant="warning"
        />
        <StatCard
          title="Documents Signed"
          value={documents ? formatNumber(documents.documents_used) : '—'}
          subtitle={
            !documents
              ? 'No active plan'
              : documents.unlimited
                ? 'Unlimited documents'
                : `of ${formatNumber(documents.document_limit)} · ${formatNumber(documents.documents_remaining)} left`
          }
          icon={<CheckCircleIcon />}
          variant="default"
        />
      </div>

      {/* Document usage */}
      {documents && (
        <Card
          title="Document Usage"
          subtitle={
            documents.unlimited
              ? `Unlimited documents for all ${documents.licences_total} licence${documents.licences_total === 1 ? '' : 's'}`
              : documents.is_trial
                ? `Your free trial includes ${formatNumber(documents.document_limit)} documents`
                : `${formatNumber(documents.document_limit)} documents shared by ${documents.licences_total} licence${documents.licences_total === 1 ? '' : 's'}`
          }
          headerAction={
            <Button variant="ghost" size="sm" onClick={() => navigate('/subscription#license-tokens')}>
              Manage Licences
            </Button>
          }
        >
          <div className={styles.usageSection}>
            <UsageMeter
              size="lg"
              label={documents.is_trial ? 'Free trial documents' : 'All licences'}
              used={documents.documents_used}
              limit={documents.document_limit}
              unlimited={documents.unlimited}
              caption={documents.unlimited ? 'No limit until your plan ends' : `${formatNumber(documents.documents_remaining)} documents left`}
            />
            {!documents.is_trial && licenceList.length > 0 && (
              <div className={styles.licenceUsage}>
                {licenceList.slice(0, MAX_LICENCES_SHOWN).map((licence) => (
                  <UsageMeter
                    key={licence.token_id}
                    size="sm"
                    label={licence.name}
                    used={licence.documents_used}
                    limit={licence.document_limit}
                    unlimited={documents.unlimited}
                  />
                ))}
                {licenceList.length > MAX_LICENCES_SHOWN && (
                  <span className={styles.moreLicences}>
                    +{licenceList.length - MAX_LICENCES_SHOWN} more licences on the Subscription page
                  </span>
                )}
              </div>
            )}
            {!documents.is_trial && licenceList.length === 0 && (
              <p className={styles.usageHint}>
                Generate a token for each computer to start signing.
                {!documents.unlimited && ` Each licence gets ${formatNumber(documents.default_per_licence)} documents by default.`}
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Subscription Status */}
      {!subscription && !showExpiredTrial && (
        <Card className={styles.noSubscription}>
          <EmptyState
            icon={<SubscriptionIcon size={48} />}
            title="No active subscription"
            description="Get started with a subscription plan to access all features"
            action={() => navigate('/plans')}
            actionLabel="View Plans"
          />
        </Card>
      )}

      {/* Recent Transactions */}
      <Card
        title="Recent Transactions"
        headerAction={
          recentTransactions.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/payment-history')}>
              View All
            </Button>
          )
        }
        padding="none"
      >
        {recentTransactions.length === 0 ? (
          <EmptyState
            icon={<TransactionIcon size={36} />}
            title="No transactions yet"
            description="Your transaction history will appear here"
          />
        ) : (
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Header>Transaction ID</Table.Header>
                <Table.Header>Date</Table.Header>
                <Table.Header>Plan</Table.Header>
                <Table.Header align="right">Amount</Table.Header>
                <Table.Header align="center">Status</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {recentTransactions.map((tx) => (
                <Table.Row key={tx.id || tx.transaction_id}>
                  <Table.Cell>{tx.transaction_id || tx.id}</Table.Cell>
                  <Table.Cell>{formatDate(tx.created_at || tx.date)}</Table.Cell>
                  <Table.Cell>{tx.plan?.name || tx.plan || '—'}</Table.Cell>
                  <Table.Cell align="right">{formatCurrency(tx.amount)}</Table.Cell>
                  <Table.Cell align="center">{getStatusBadge(tx.status)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Card>
    </div>
  );
}

export default Dashboard;
